import React, { useMemo } from 'react';

import { Navigate, useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag, OfferType } from 'constants/enums';

import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { useOfferTable } from '../../hooks/useOfferTable';
import { OfferPageLayout } from '../../components/OfferPageLayout';

import { useDailyBonusColumns } from './hooks/useDailyBonusColumns';

const DailyBonusTable = (): React.ReactElement | null => {
  const { publisherId } = useParams();
  const { featureFlags, isLoading } = useFeatureFlags({ publisherId });

  if (isLoading) {
    return null;
  }

  if (!featureFlags?.[EFeatureFlag.DASHBOARD_DAILY_BONUS_SIDE_MENU]) {
    return <Navigate to="../popups" replace />;
  }

  return <DailyBonusTableContent />;
};

const DailyBonusTableContent = (): React.ReactElement => {
  const {
    getOffers,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    externalIdFilter,
    setExternalIdFilter,
    nameFilter,
    setNameFilter,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    offers,
    processOffers
  } = useOfferTable({
    offerType: OfferType.POPUP,
    offerSubType: 'DailyBonus',
    offerLabel: 'Daily Bonus',
    needsPricings: false
  });

  const { columns } = useDailyBonusColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions
  });

  const processed = useMemo(
    () => processOffers(offers),
    [offers, processOffers]
  );

  return (
    <OfferPageLayout
      title="Daily Bonus"
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      offerLabel="Daily Bonus"
      showFilters={offers.length > 0}
      externalIdFilter={externalIdFilter}
      onExternalIdFilterChange={setExternalIdFilter}
      nameFilter={nameFilter}
      onNameFilterChange={setNameFilter}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle="Add your first Daily Bonus"
      emptyStateDescription="Daily bonus offers that reward players for logging in every day"
      createButtonPrefix="Daily Bonus"
      bodyDataTestId="dashDailyBonusList"
      offerType={OfferType.POPUP}
      offerDesignType={OfferDesignTypes.PopUp}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle="Delete Daily Bonus"
      deleteModalDescription="Are you sure you want to delete this daily bonus?"
    />
  );
};

export { DailyBonusTable };
